'use client';

import { AspectRatio } from '@ironhack/design-system2/components/aspect-ratio';
import { Box } from '@ironhack/design-system2/components/box';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Link } from '@ironhack/design-system2/components/link';
import {
  ListItem,
  OrderedList,
  UnorderedList,
} from '@ironhack/design-system2/components/list';
import { Skeleton } from '@ironhack/design-system2/components/skeleton';
import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@ironhack/design-system2/components/table';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import {
  isBlockquote,
  isHeading,
  isLink,
  isList,
  isListItem,
  isParagraph,
} from 'datocms-structured-text-utils';
import {
  Children,
  type ReactElement,
  cloneElement,
  isValidElement,
} from 'react';
import {
  Image,
  StructuredText,
  type StructuredTextGraphQlResponse,
  renderMarkRule,
  renderRule,
} from 'react-datocms';
import ReactPlayer from 'react-player/lazy';

import { Button } from '@/app/components/client/button';
import { updateUrlRegion } from '@/app/components/client/link/link-utils';
import { ListWithIcons } from '@/app/components/server/list-with-icons';
import { type DatoCustomBlock } from '@/lib/datocms/types';

type Props = FlexProps & {
  color?: FlexProps['color'];
  data: StructuredTextGraphQlResponse;
  // Ignore color so that they don't override styles
  ignoreLinkColor?: boolean;
  // Ignore inline text styles so that they don't override heading styles
  ignoreInlineTextStyle?: boolean;
  textStyle?: string;
};

const headingStyleMap = {
  1: '6xl',
  2: '5xl',
  3: '4xl',
  4: '3xl',
  5: '2xl',
  6: 'xl',
};

export const StructuredContent = (props: Props): ReactElement => {
  const {
    data,
    textStyle = 'l',
    color = 'text.primary',
    noOfLines,
    ignoreLinkColor = true,
    ignoreInlineTextStyle = false,
    ...flexProps
  } = props;
  const renderBlock = (record: DatoCustomBlock): ReactElement | null => {
    switch (record.__typename) {
      case 'ButtonRecord': {
        return (
          <Flex alignSelf="center" my={3}>
            <Button {...record} />
          </Flex>
        );
      }

      case 'ImageBlockRecord': {
        return (
          <Flex alignSelf="center" my={3}>
            <Image
              data={record.image.responsiveImage}
              style={{ borderRadius: '16px' }}
            />
          </Flex>
        );
      }

      case 'ListWithIconsBlockRecord': {
        return <ListWithIcons color={color} list={record} />;
      }

      case 'TableBlockRecord': {
        return (
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  {record.table.columns.map((column) => (
                    <Th key={column}>{column}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {record.table.data.map((row, index) => (
                  <Tr key={index}>
                    {record.table.columns.map((column) => (
                      <Td key={column}>{row[column]}</Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        );
      }

      case 'VideoBlockRecord': {
        return (
          <Flex justifyContent="center" my={3}>
            <AspectRatio flex={1} maxW="800px" ratio={16 / 9}>
              <ReactPlayer
                controls
                fallback={<Skeleton h="100%" />}
                height="100%"
                light
                style={{ borderRadius: '16px' }}
                url={record.videoUrl}
                width="100%"
              />
            </AspectRatio>
          </Flex>
        );
      }

      default: {
        return null;
      }
    }
  };

  return (
    <Flex direction="column" gap={1} w="full" {...flexProps}>
      <StructuredText
        customMarkRules={[
          renderMarkRule('strong', ({ children, key }) => (
            <Text
              as="strong"
              color={color}
              key={key}
              textStyle={ignoreInlineTextStyle ? 'bold' : `${textStyle}Bold`}
              {...(noOfLines ? { noOfLines } : {})}
            >
              {children}
            </Text>
          )),
        ]}
        customRules={[
          renderRule(isLink, ({ key, children, node }) => {
            const isExternal = node.meta?.some(
              (m) => m.id === 'target' && m.value === '_blank'
            );
            return (
              <Link
                {...(color && {
                  _hover: { color, textDecoration: 'underline' },
                })}
                href={updateUrlRegion(node.url)}
                isExternal={isExternal}
                key={key}
              >
                <Text
                  as="span"
                  {...(!ignoreLinkColor && { color })}
                  {...(!ignoreInlineTextStyle && { textStyle })}
                >
                  {children}
                </Text>
              </Link>
            );
          }),
          renderRule(isParagraph, ({ children, key }) => (
            <Text
              color={color}
              key={key}
              textStyle={textStyle}
              {...(noOfLines ? { noOfLines } : {})}
            >
              {children}
            </Text>
          )),
          renderRule(isBlockquote, ({ children, key }) => (
            <Text
              // Make this a different element to prevent nested p tags
              as="blockquote"
              borderLeftColor="stroke.interactive"
              borderLeftWidth="4px"
              key={key}
              pl={1.5}
            >
              {Children.map(children, (child) =>
                isValidElement<TextProps>(child)
                  ? cloneElement<TextProps>(child, {
                      textStyle: 'xl',
                    })
                  : child
              )}
            </Text>
          )),
          renderRule(isHeading, ({ node, children, key }) => (
            <Text
              as={`h${node.level}`}
              color={color}
              key={key}
              my={3}
              textStyle={headingStyleMap[node.level]}
            >
              {children}
            </Text>
          )),
          renderRule(isList, ({ node, children, key }) => (
            <Box key={key}>
              {node?.style === 'numbered' ? (
                <OrderedList mb={3} spacing={0}>
                  {children}
                </OrderedList>
              ) : (
                <UnorderedList mb={3} spacing={0}>
                  {children}
                </UnorderedList>
              )}
            </Box>
          )),
          renderRule(isListItem, ({ children, key }) => (
            <ListItem key={key}>{children}</ListItem>
          )),
        ]}
        data={data}
        // @ts-expect-error -- Don't know how to type this. Feel free to check.
        renderBlock={({
          record,
        }: {
          record: DatoCustomBlock;
        }): ReturnType<typeof renderBlock> => renderBlock(record)}
      />
    </Flex>
  );
};
